import { autocompleteClasses } from '@mui/material/Autocomplete';
import { checkboxClasses } from '@mui/material/Checkbox';
import { dividerClasses } from '@mui/material/Divider';
import { menuItemClasses } from '@mui/material/MenuItem';
import type { CSSObject, Theme } from '@mui/material/styles';

import { varAlpha } from 'minimal-shared/utils';

/**
 * Generates styles for menu items.
 *
 * @param {Theme} theme - The theme object.
 * @returns {CSSObject} The CSS object for menu item styles.
 *
 * @example
 * ...theme.mixins.menuItemStyles(theme)
 */
export function menuItemStyles(theme: Theme): CSSObject {
    return {
        ...theme.typography.body2,
        padding: theme.spacing(0.75, 1),
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        borderRadius: theme.shape.borderRadius * 0.75,
        '&:not(:last-of-type)': {
            marginBottom: 4,
        },
        [`&.${menuItemClasses.selected}`]: {
            fontWeight: theme.typography.fontWeightSemiBold,
            backgroundColor: theme.vars.palette.action.selected,
            '&:hover': { backgroundColor: theme.vars.palette.action.hover },
        },
        [`& .${checkboxClasses.root}`]: {
            padding: theme.spacing(0.5),
            marginLeft: theme.spacing(-0.5),
            marginRight: theme.spacing(0.5),
        },
        [`&.${autocompleteClasses.option}[aria-selected="true"]`]: {
            backgroundColor: theme.vars.palette.action.selected,
            '&:hover': { backgroundColor: theme.vars.palette.action.hover },
        },
        [`&+.${dividerClasses.root}`]: {
            margin: theme.spacing(0.5, 0),
        },
    };
}

/**
 * Generates styles for paper components.
 *
 * @param {PaperStyleOptions} props - The properties for the paper styles.
 * @param {Theme} props.theme - The theme object.
 * @param {string} [props.color] - The background color.
 * @param {boolean} [props.dropdown] - Whether the paper is a dropdown.
 * @returns {CSSObject} The CSS object for paper styles.
 *
 * @example
 * ...theme.mixins.paperStyles(theme, { dropdown: true, blur: 20, color: varAlpha(theme.vars.palette.background.defaultChannel, 0.9) }),
 */
export type PaperStyleOptions = {
    blur?: number;
    color?: string;
    dropdown?: boolean;
};

/**
 * Tools for creating image base64
 * https://www.fffuel.co/eeencode/
 */
const cyanShape =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIiBmaWxsPSJ1cmwoI3BhaW50MF9yYWRpYWxfNDQ2NF81NTMzOCkiIGZpbGwtb3BhY2l0eT0iMC4xIi8+CjxkZWZzPgo8cmFkaWFsR3JhZGllbnQgaWQ9InBhaW50MF9yYWRpYWxfNDQ2NF81NTMzOCIgY3g9IjAiIGN5PSIwIiByPSIxIiBncmFkaWVudFVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgZ3JhZGllbnRUcmFuc2Zvcm09InRyYW5zbGF0ZSgxMjAgMS44MTgxMmUtMDUpIHJvdGF0ZSgtNDUpIHNjYWxlKDEyMy4yNSkiPgo8c3RvcCBzdG9wLWNvbG9yPSIjMDBCOEQ5Ii8+CjxzdG9wIG9mZnNldD0iMSIgc3RvcC1jb2xvcj0iIzAwQjhEOSIgc3RvcC1vcGFjaXR5PSIwIi8+CjwvcmFkaWFsR3JhZGllbnQ+CjwvZGVmcz4KPC9zdmc+Cg==';

const redShape =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIiBmaWxsPSJ1cmwoI3BhaW50MF9yYWRpYWxfNDQ2NF81NTMzNykiIGZpbGwtb3BhY2l0eT0iMC4xIi8+CjxkZWZzPgo8cmFkaWFsR3JhZGllbnQgaWQ9InBhaW50MF9yYWRpYWxfNDQ2NF81NTMzNyIgY3g9IjAiIGN5PSIwIiByPSIxIiBncmFkaWVudFVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgZ3JhZGllbnRUcmFuc2Zvcm09InRyYW5zbGF0ZSgwIDEyMCkgcm90YXRlKDEzNSkgc2NhbGUoMTIzLjI1KSI+CjxzdG9wIHN0b3AtY29sb3I9IiNGRjU2MzAiLz4KPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjRkY1NjMwIiBzdG9wLW9wYWNpdHk9IjAiLz4KPC9yYWRpYWxHcmFkaWVudD4KPC9kZWZzPgo8L3N2Zz4K';

/**
 * Generates CSS object for paper styled elements with customizable backdrop blur and colors.
 *
 * @param theme - The theme object to extract styling variables from
 * @param options - Optional configuration options for the paper styles
 * @param options.blur - The intensity of the backdrop blur effect in pixels (default: 20)
 * @param options.color - Custom background color, defaults to semi-transparent paper background
 * @param options.dropdown - Whether to apply additional dropdown-specific styling
 * @returns CSS object with paper styling properties
 */
export function paperStyles(theme: Theme, options?: PaperStyleOptions): CSSObject {
    const { blur = 20, color, dropdown } = options ?? {};
    return {
        ...theme.mixins.bgGradient({
            images: [`url(${cyanShape})`, `url(${redShape})`],
            sizes: ['50%', '50%'],
            positions:
                theme.direction === 'rtl'
                    ? ['top left', 'right bottom']
                    : ['top right', 'left bottom'],
        }),
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`,
        backgroundColor: color ?? varAlpha(theme.vars.palette.background.paperChannel, 0.9),
        ...(dropdown && {
            padding: theme.spacing(0.5),
            boxShadow: theme.vars.customShadows.dropdown,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            borderRadius: `${theme.shape.borderRadius * 1.25}px`,
        }),
    };
}
